import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Text } from "@/atoms";
import { useCountUp } from "@/hooks/useCountUp";
import { RewardProgressBar } from "@/modules/onboarding/components/RewardProgressBar/RewardProgressBar";
import { get } from "api";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import CheckSmall from "atoms/icon/icons/ic_check_small_ol.svg";
import QrCode from "atoms/icon/icons/qr_code.svg";
import RepeatMD from "atoms/icon/icons/RepeatCash.svg";
import UserNav from "atoms/icon/icons/user_nav.svg";
import { formatCurrency } from "toolbox/Money";
import pluralize from "utils/pluralize";

export type RewardProgressV2Props = {
  rewardsCount: number;
  nextMilestone: number;
  unlockProgressPercent: number;
  cashToUnlock?: number;
  marketplaceEnabled?: boolean;
  visitCashValue?: number;
  spendBasedRewardsEnabled: boolean;
  hideRepeatCash?: boolean;
  displayGreetings?: boolean;
  canCollapse?: boolean;
  showCountUpAnimation?: boolean;
};

export const RewardProgressV2 = ({
  rewardsCount,
  nextMilestone,
  unlockProgressPercent,
  cashToUnlock,
  marketplaceEnabled,
  visitCashValue,
  spendBasedRewardsEnabled,
  hideRepeatCash = false,
  displayGreetings = false,
  canCollapse = false,
  showCountUpAnimation = false,
}: RewardProgressV2Props) => {
  const navigate = useNavigate();
  const { data: userCanCheckIn } = useQuery({
    queryKey: ["/user/check-in"],
    queryFn: () => get("/user/check-in"),
  });

  const { data: user } = useQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  const { data: platform } = useQuery({
    queryKey: ["/platform"],
    queryFn: () => get("/platform"),
  });

  const [isScrolled, setIsScrolled] = useState(false);
  const SCROLL_THRESHOLD = 5; // approximate height of banners

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > SCROLL_THRESHOLD);
    };

    window.addEventListener("scroll", handleScroll);

    // Check initial scroll position
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [SCROLL_THRESHOLD]);

  const repeatCashBalance = Number(user?.repeatCashBalance ?? 0);

  const countUpValue = useCountUp({
    to: repeatCashBalance,
    from: showCountUpAnimation ? 0 : repeatCashBalance,
    duration: 1500,
    delay: 0,
  });

  if (!user) {
    return null;
  }

  const userMessage = (): string => {
    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    const greetings =
      currentHour < 12
        ? "Good Morning"
        : currentHour < 18
        ? "Good Afternoon"
        : "Good Evening";
    if (user.firstName) {
      return `${greetings}, ${user.firstName}`.toUpperCase();
    }
    return greetings;
  };

  const getTitleText = (): string | undefined => {
    if (user.isGuest) {
      return "Unlock exclusive rewards—join now!";
    }

    if (nextMilestone === 0 && rewardsCount === 0) {
      return undefined;
    }

    if (
      !marketplaceEnabled ||
      visitCashValue === 0 ||
      !spendBasedRewardsEnabled
    ) {
      return `Almost there! Just ${nextMilestone} ${pluralize(
        nextMilestone,
        "visit",
        "visits"
      )} left to hit your next reward.`;
    }

    if (nextMilestone) {
      return `Almost there! Just ${nextMilestone} ${pluralize(
        nextMilestone,
        "visit",
        "visits"
      )}${
        cashToUnlock ? ` or ${formatCurrency(cashToUnlock)}` : ""
      } left to hit your next reward.`;
    }

    if (rewardsCount > 0) {
      return `You have ${rewardsCount} reward${
        rewardsCount > 1 ? "s" : ""
      } waiting!`;
    }

    return undefined;
  };

  const renderRepeatCash = () => {
    if (hideRepeatCash || user.isGuest) {
      return null;
    }

    return (
      <button
        className="flex items-center gap-2 rounded-full border border-white bg-transparent px-3 py-1 transition-colors hover:bg-white/10"
        type="button"
        onClick={() => navigate("/rewards")}
      >
        <Icon svg={RepeatMD} size="small" />
        <Text
          variant="body1"
          component="p"
          className="font-bold"
          text={formatCurrency(countUpValue, 0)}
        />
      </button>
    );
  };

  const renderYouHaveScannedBanner = () => (
    <div className="flex items-center gap-1 rounded-full bg-background-two py-2 pl-3 pr-3">
      <Icon svg={CheckSmall} color="secondaryText" size="x2small" />
      <Text
        variant="body3"
        component="p"
        className=" text-secondary"
        text="You've checked-in today."
      />
    </div>
  );

  const renderScanInButton = () => {
    const handleClick = () =>
      navigate(user.isGuest ? "/signin" : "/scan-checkin-page");
    const icon = user.isGuest ? UserNav : QrCode;
    const hasNextReward = nextMilestone > 0 || rewardsCount > 0;
    const buttonText = user.isGuest
      ? "Sign up to start earning"
      : hasNextReward
      ? "Check-in for rewards"
      : "Check-in";

    return (
      <Button
        endIconSVG={icon}
        onClick={handleClick}
        size="small"
        style="white"
        iconSize="xsmall"
      >
        <p className="sans-serif mr-2 text-sm font-semibold">{buttonText}</p>
      </Button>
    );
  };

  const titleText = getTitleText();
  const shouldHideProgress =
    nextMilestone === 0 && rewardsCount === 0 && !user.isGuest;

  const renderGreeting = () =>
    displayGreetings && (
      <Text
        variant="body1"
        component="p"
        className="font-bold text-brand-color"
        text={userMessage()}
      />
    );
  return (
    <div
      className={cx(
        "RewardProgressV2 flex flex-col gap-3 px-5  pt-3 text-center shadow-lg",
        shouldHideProgress && "pb-4"
      )}
    >
      {renderGreeting()}
      <div className="flex items-center justify-between">
        {renderRepeatCash()}
        {userCanCheckIn ? renderScanInButton() : renderYouHaveScannedBanner()}
      </div>

      {!shouldHideProgress && (
        <>
          <RewardProgressBar progress={unlockProgressPercent} />

          <motion.div
            animate={{
              height: canCollapse && isScrolled ? 0 : "auto",
              opacity: canCollapse && isScrolled ? 0 : 1,
            }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
          >
            <div className="flex flex-col gap-2 pb-4">
              {titleText && (
                <Text
                  variant="v3-h4"
                  component="p"
                  className="w-full text-center"
                  text={titleText}
                />
              )}

              {!user.isGuest && spendBasedRewardsEnabled && (
                <Text
                  variant="body2"
                  component="p"
                  className="w-full text-center text-secondary"
                  text="Check in every visit or shop in-app for rewards"
                />
              )}
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
};
